'use client';

/**
 * CostTimeToTargetCard — mode6's runway / time-to-budget view.
 *
 * The bell-curve sibling of the forecast card. Same Monte Carlo
 * simulations, different projection of the trajectory tensor:
 * instead of "what's spend by date T?" this answers "when do we
 * first cross threshold X?". Output is a histogram of crossing
 * dates with vertical reference lines at P5 / P50 / P95, plus a
 * one-line summary callout above the chart.
 *
 * Reads `cost_time_to_target` via the capability matrix —
 * (category=cost_analytics, action=time_to_target).
 *
 * @see klyde_edge/ui_config/composition/modes/mode6.py
 *      content_key="cost_time_to_target_canvas"
 * @see klyde_edge/klyde_costs/responses.py — CostTimeToTargetResponse
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useMemo, useRef } from 'react';
import {
  Loader2, XCircle, Target, DollarSign, Cpu, ImageIcon, Users, Download,
} from 'lucide-react';
import {
  Bar, ComposedChart, CartesianGrid, Line, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis, Cell,
} from 'recharts';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useFinopsLabel, getFinopsLabel } from '@/lib/stores/total-matrix-store';
import { exportCardAsPng, exportCardAsSvg, todayStamp } from '@/lib/utils/export-chart';
import { CardPlacementProvider, useCardExport, useCardPlacement } from '@/lib/design/card-export';
import { text } from '@/lib/design/tokens';
import { useFinopsStrategyData } from './use-finops-query';
import { useFinopsTtsStore } from '@/lib/stores/finops-tts-store';
import { cn } from '@/lib/utils/cn';
import type { CostTimeToTargetResponse, CostTimeToTargetSeries } from '@/lib/api/types';

// Series picker chrome — i18n labelKey + icon per series. Resolved
// at render via `useFinopsLabel(spec.labelKey)`.
type SeriesKey = 'total' | 'fixed' | 'gemini' | 'vertex';
const SERIES_SPECS: Record<SeriesKey, { labelKey: string; icon: typeof DollarSign }> = {
  total:  { labelKey: 'series_total',  icon: Target },
  fixed:  { labelKey: 'series_fixed',  icon: DollarSign },
  gemini: { labelKey: 'series_gemini', icon: Cpu },
  vertex: { labelKey: 'series_vertex', icon: ImageIcon },
};


// ─────────────────────────────────────────────────────────────────────
// Card
// ─────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────
// Shared simulation hook — one deduped query, store-driven levers.
// Every tts card calls this; React Query collapses them to ONE fetch.
// ─────────────────────────────────────────────────────────────────────

function useTtsSimulation() {
  const ttsStrategy = useCapabilityStore((s) => s.strategyName('cost_analytics', 'time_to_target'));
  const targetChf = useFinopsTtsStore((s) => s.targetChf);
  const usersProjected = useFinopsTtsStore((s) => s.usersProjected);
  const selectedSeries = useFinopsTtsStore((s) => s.selectedSeries);
  const { windowDays } = useMemo(() => yearScopeDays(), []);

  const simParams = {
    target_chf: targetChf,
    window_days: windowDays,
    n_simulations: 10_000,
    seed: 42,
    max_horizon_days: 1_500,   // wide enough for low-burn vs cash-balance scenarios
    include_ytd: false,
    ...(usersProjected != null ? { n_users_projected: usersProjected } : {}),
  };
  const simQuery = useFinopsStrategyData<CostTimeToTargetResponse>(ttsStrategy, simParams);
  const data = simQuery.data?.success ? simQuery.data.data ?? null : null;
  const error = simQuery.data && !simQuery.data.success
    ? simQuery.data.error || 'Failed to load runway analysis'
    : simQuery.error
      ? simQuery.error.message ?? 'Network error'
      : null;
  const isLoading = simQuery.isFetching;
  const activeSeries: CostTimeToTargetSeries | null = useMemo(() => {
    if (!data || data.series.length === 0) return null;
    return data.series.find((se) => se.name === selectedSeries) ?? data.series[0];
  }, [data, selectedSeries]);

  return { ttsStrategy, simQuery, data, error, isLoading, activeSeries, targetChf, usersProjected, selectedSeries };
}

// ─────────────────────────────────────────────────────────────────────
// cost_tts_header — identity + answer + simulation levers (a card)
// ─────────────────────────────────────────────────────────────────────

export function CostTtsHeaderCard() {
  const sim = useTtsSimulation();
  const setSelectedSeries = useFinopsTtsStore((s) => s.setSelectedSeries);
  const setTargetChf = useFinopsTtsStore((s) => s.setTargetChf);
  const setUsersProjected = useFinopsTtsStore((s) => s.setUsersProjected);
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Export the whole composite (all three sibling cards) — resolved via
  // the composite root's data attribute, not a plumbed ref.
  const compositeEl = () =>
    (rootRef.current?.closest('[data-tts-composite]') as HTMLElement | null) ?? rootRef.current;
  const handleExportPng = () => {
    exportCardAsPng(compositeEl(), `klyde-time-to-target-${todayStamp()}.png`)
      .catch(() => alert(getFinopsLabel('export_failed')));
  };
  const handleExportSvg = () => {
    exportCardAsSvg(compositeEl(), `klyde-time-to-target-${todayStamp()}.svg`)
      .catch(() => alert(getFinopsLabel('export_failed')));
  };

  const loadingLabel = useFinopsLabel('loading_simulation');
  const emptyLabel = useFinopsLabel('empty_set_target');
  const seriesLabel = {
    total:  useFinopsLabel(SERIES_SPECS.total.labelKey),
    fixed:  useFinopsLabel(SERIES_SPECS.fixed.labelKey),
    gemini: useFinopsLabel(SERIES_SPECS.gemini.labelKey),
    vertex: useFinopsLabel(SERIES_SPECS.vertex.labelKey),
  } as const;

  const { data, error, isLoading, activeSeries } = sim;
  useCardExport({
    label: 'Time to target',
    content_key: 'cost_tts_header',
    text: activeSeries
      ? `Runway to CHF ${sim.targetChf.toLocaleString()} (${activeSeries.name}): ` +
        `P50 ${activeSeries.p50_date ?? 'beyond horizon'} (day ${activeSeries.p50_days}) · ` +
        `range ${activeSeries.p5_date ?? '—'} – ${activeSeries.p95_date ?? '—'} (P5–P95)`
      : `Runway to CHF ${sim.targetChf.toLocaleString()} — no simulation data`,
    json: activeSeries
      ? {
          target: `CHF ${sim.targetChf.toLocaleString()}`,
          series: activeSeries.name,
          p50: `${activeSeries.p50_date ?? 'beyond horizon'} (day ${activeSeries.p50_days})`,
          p_crossed_by_max_horizon: `${Math.round(activeSeries.p_crossed_by_max_horizon * 100)}%`,
          users_projected: data ? String(data.n_users_projected) : null,
        }
      : undefined,
    state: { series: sim.selectedSeries, target_chf: sim.targetChf, users_projected: sim.usersProjected },
    status: error ? 'error' : data ? 'loaded' : isLoading ? 'loading' : 'idle',
    error: error ?? undefined,
    query_key: sim.ttsStrategy ? sim.simQuery.queryKey : null,
  });

  if (isLoading && !data) {
    return (
      <div ref={rootRef} className="rounded-lg border border-border bg-card p-4 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {loadingLabel}
      </div>
    );
  }
  if (error) {
    return (
      <div ref={rootRef} className="rounded-lg border border-border bg-card p-4 flex items-center gap-2 text-destructive">
        <XCircle className="h-4 w-4" />
        <span className={text.body}>{error}</span>
      </div>
    );
  }
  if (!data || !activeSeries) {
    return (
      <div ref={rootRef} className="rounded-lg border border-border bg-card p-4 flex items-center gap-2 text-muted-foreground">
        <Target className="h-4 w-4 opacity-40" />
        <span className={text.body}>{emptyLabel}</span>
      </div>
    );
  }
  return (
    <div ref={rootRef} className="rounded-lg border border-border bg-card px-3 py-2 flex flex-col gap-1.5">
      <Header data={data} onExportPng={handleExportPng} onExportSvg={handleExportSvg} />
      <SummaryCallout data={data} active={activeSeries} seriesLabelMap={seriesLabel} />
      {/* One wrapping chip row, ordered so the natural break is
          Series+Users (~620px, fit side by side) then the wide Cash
          group — 2 lines at overlay width, 1 line when wider. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
        <SeriesPicker value={sim.selectedSeries} onChange={setSelectedSeries} series={data.series} />
        <UserScaleControl
          observed={data.n_users_observed}
          projected={data.n_users_projected}
          selected={sim.usersProjected}
          onChange={setUsersProjected}
        />
        <TargetControl value={sim.targetChf} onChange={setTargetChf} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// cost_tts_chart — the histogram + CDF, nothing else (a card)
// ─────────────────────────────────────────────────────────────────────

export function CostTtsChartCard() {
  const { ttsStrategy, simQuery, data, activeSeries } = useTtsSimulation();
  const cdfLegend = useFinopsLabel('tts_tooltip_cdf_legend');
  const simsBinLegend = useFinopsLabel('tts_tooltip_sims_in_bin');
  const yLabelSims = useFinopsLabel('tts_yaxis_sims');
  const yLabelCrossed = useFinopsLabel('tts_yaxis_p_crossed');

  const chartData = useMemo(() => {
    if (!activeSeries || activeSeries.histogram.length === 0) return [];
    const totalSims = data?.n_simulations ?? 1;
    let cumFreq = 0;
    return activeSeries.histogram.map((b) => {
      cumFreq += b.frequency;
      return {
        date_mid: b.date_mid,
        day: b.day_offset_mid,
        frequency: b.frequency,
        cdf_pct: (cumFreq / totalSims) * 100,
        range: `day ${b.day_offset_low}–${b.day_offset_high}`,
      };
    });
  }, [activeSeries, data?.n_simulations]);

  useCardExport({
    label: 'Runway histogram',
    content_key: 'cost_tts_chart',
    text: activeSeries
      ? `Crossing-date histogram (${activeSeries.name}) — ${chartData.length} bins`
      : '',
    status: activeSeries ? 'loaded' : 'idle',
    query_key: ttsStrategy ? simQuery.queryKey : null,
  });

  if (!activeSeries || activeSeries.histogram.length === 0) return null;
  const peakBin = activeSeries.histogram.reduce(
    (max, b) => (b.frequency > max.frequency ? b : max),
    activeSeries.histogram[0],
  );
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="h-[300px] shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 16, right: 56, bottom: 8, left: 0 }}>
            <CartesianGrid strokeOpacity={0.15} vertical={false} />
            <XAxis
              dataKey="date_mid"
              tick={{ fontSize: 10 }}
              tickFormatter={shortDate}
              minTickGap={36}
              interval="preserveStartEnd"
            />
            {/* Left Y: histogram frequency (count of simulations per bin) */}
            <YAxis
              yAxisId="freq"
              tick={{ fontSize: 10 }}
              tickFormatter={(v: number) => v.toLocaleString()}
              width={48}
              label={{ value: yLabelSims, angle: -90, position: 'insideLeft', fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            />
            {/* Right Y: cumulative density (P(crossed by this date), 0–100%) */}
            <YAxis
              yAxisId="cdf"
              orientation="right"
              tick={{ fontSize: 10 }}
              tickFormatter={(v: number) => `${v.toFixed(0)}%`}
              domain={[0, 100]}
              width={44}
              label={{ value: yLabelCrossed, angle: 90, position: 'insideRight', fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            />
            <Tooltip
              labelFormatter={(d) => getFinopsLabel('tts_tooltip_label_by', { date: shortDate(String(d)) })}
              formatter={(v: number, name: string) => {
                if (name === 'CDF') return [`${v.toFixed(1)}%`, cdfLegend];
                return [getFinopsLabel('tts_tooltip_sims_value', { n: v.toLocaleString() }), simsBinLegend];
              }}
              contentStyle={{ fontSize: 12 }}
            />

            {/* P5 / P50 / P95 reference lines on the left axis — sourced
                from the active series (so they re-render when the picker
                flips between total / fixed / gemini / vertex). */}
            {activeSeries.p5_date && (
              <ReferenceLine
                yAxisId="freq"
                x={nearestBinDate(activeSeries.histogram, activeSeries.p5_days)}
                stroke="#f59e0b"
                strokeDasharray="3 3"
                label={{ value: 'P5', position: 'top', fontSize: 10, fill: '#f59e0b' }}
              />
            )}
            {activeSeries.p50_date && (
              <ReferenceLine
                yAxisId="freq"
                x={nearestBinDate(activeSeries.histogram, activeSeries.p50_days)}
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                label={{ value: 'P50', position: 'top', fontSize: 10, fill: 'hsl(var(--primary))' }}
              />
            )}
            {activeSeries.p95_date && (
              <ReferenceLine
                yAxisId="freq"
                x={nearestBinDate(activeSeries.histogram, activeSeries.p95_days)}
                stroke="#ef4444"
                strokeDasharray="3 3"
                label={{ value: 'P95', position: 'top', fontSize: 10, fill: '#ef4444' }}
              />
            )}

            {/* Histogram bars — frequency per bin, peak bin highlighted */}
            <Bar yAxisId="freq" dataKey="frequency" name="Simulations" isAnimationActive={false}>
              {chartData.map((d) => (
                <Cell
                  key={d.date_mid}
                  fill={d.day === peakBin.day_offset_mid ? '#6366f1' : '#94a3b8'}
                />
              ))}
            </Bar>

            {/* CDF — running cumulative %. Right Y-axis (0..100). The
                line caps at p_crossed_by_max_horizon * 100 — if some sims
                didn't cross within max_horizon, the CDF visibly plateaus
                below 100%, telling the reader exactly that. */}
            <Line
              yAxisId="cdf"
              type="monotone"
              dataKey="cdf_pct"
              name="CDF"
              stroke="#10b981"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// cost_tts_kpis — P5 / P50 / P95 tiles (a card)
// ─────────────────────────────────────────────────────────────────────

export function CostTtsKpisCard() {
  const { ttsStrategy, simQuery, activeSeries } = useTtsSimulation();
  useCardExport({
    label: 'Runway percentiles',
    content_key: 'cost_tts_kpis',
    text: activeSeries
      ? `P5 ${activeSeries.p5_date ?? '—'} · P50 ${activeSeries.p50_date ?? '—'} · P95 ${activeSeries.p95_date ?? '—'}`
      : '',
    json: activeSeries
      ? { p5: activeSeries.p5_date, p50: activeSeries.p50_date, p95: activeSeries.p95_date }
      : undefined,
    status: activeSeries ? 'loaded' : 'idle',
    query_key: ttsStrategy ? simQuery.queryKey : null,
  });
  if (!activeSeries) return null;
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <ScalarStrip active={activeSeries} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// cost_time_to_target_canvas — the composite root (collection card):
// mounts the three sibling cards, each with its own placement identity.
// Layout intent lives with the placements/preset, data in ONE deduped
// query, levers in the finops-tts store. No hand-crafted internals.
// ─────────────────────────────────────────────────────────────────────

export function CostTimeToTargetCard() {
  const placement = useCardPlacement();
  const surface = placement?.surface ?? 'canvas';
  const base = placement?.slot_id ?? 'cost_time_to_target';
  return (
    <div data-tts-composite className="flex flex-col gap-3">
      <CardPlacementProvider value={{ surface, slot_id: `${base}:header` }}>
        <CostTtsHeaderCard />
      </CardPlacementProvider>
      <CardPlacementProvider value={{ surface, slot_id: `${base}:chart` }}>
        <CostTtsChartCard />
      </CardPlacementProvider>
      <CardPlacementProvider value={{ surface, slot_id: `${base}:kpis` }}>
        <CostTtsKpisCard />
      </CardPlacementProvider>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Header
// ─────────────────────────────────────────────────────────────────────

function Header({
  data,
  onExportPng,
  onExportSvg,
}: {
  data: CostTimeToTargetResponse;
  onExportPng: () => void;
  onExportSvg: () => void;
}) {
  const title = useFinopsLabel('tts_title');
  const exportPngLabel = useFinopsLabel('export_png_label');
  const exportPngTooltip = useFinopsLabel('export_png_tooltip');
  const exportSvgLabel = useFinopsLabel('export_svg_label');
  const exportSvgTooltip = useFinopsLabel('export_svg_tooltip');
  const provenance = useFinopsLabel('forecast_provenance', {
    n_obs: data.n_observations,
    n_sims: data.n_simulations.toLocaleString(),
  });
  const provenanceTechnical = useFinopsLabel('forecast_provenance_technical', {
    seed: data.seed ?? '',
  });
  const provenanceSecondary = data.seed != null ? provenanceTechnical : '';
  const usersTooltip = useFinopsLabel('forecast_users_tooltip');
  const projectingTemplate = useFinopsLabel('forecast_users_projecting', { n_projected: '__N__' });
  const windowLine = useFinopsLabel('tts_window_label', {
    start: data.window_start ?? '',
    end:   data.window_end   ?? '',
  });
  const maxHorizonLine = useFinopsLabel('tts_max_horizon_label', { n: data.max_horizon_days });
  const showProjecting = data.n_users_projected !== data.n_users_observed;
  const [projectingPrefix, projectingSuffix] = projectingTemplate.split('__N__');
  return (
    // ONE ROW — eyebrow title left (like the quick-answer card's
    // "QUICK ANSWER · …" line), single truncating metadata line right,
    // export buttons at the end. No stacked lines.
    <div className="flex items-center gap-2 leading-none">
      <div className={cn(text.eyebrow, 'flex items-center gap-1.5 shrink-0')}>
        <Target className="h-3 w-3 text-primary shrink-0" />
        <span>{title}</span>
      </div>
      <div className={cn(text.muted, 'min-w-0 flex-1 truncate text-right leading-tight')} title={windowLine}>
        {provenance}
        {showProjecting && (
          <span title={usersTooltip}>
            {' · '}{projectingPrefix.trim()}
            <strong className="text-foreground"> {data.n_users_projected} </strong>
            {projectingSuffix.trim()}
          </span>
        )}
        {' · '}{windowLine} · {maxHorizonLine}
        {provenanceSecondary ? <> · {provenanceSecondary}</> : null}
      </div>
      <div className="shrink-0">
        <div className="flex items-center gap-1" data-export-hidden="true">
          <button
            onClick={onExportPng}
            title={exportPngTooltip}
            aria-label={exportPngTooltip}
            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-border bg-card text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors text-[11px]"
          >
            <Download className="h-3 w-3" />
            <span>{exportPngLabel}</span>
          </button>
          <button
            onClick={onExportSvg}
            title={exportSvgTooltip}
            aria-label={exportSvgTooltip}
            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-border bg-card text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors text-[11px]"
          >
            <Download className="h-3 w-3" />
            <span>{exportSvgLabel}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// SummaryCallout — the one-line "answer"
// ─────────────────────────────────────────────────────────────────────

function SummaryCallout({
  data,
  active,
  seriesLabelMap,
}: {
  data: CostTimeToTargetResponse;
  active: CostTimeToTargetSeries;
  seriesLabelMap: Readonly<Record<SeriesKey, string>>;
}) {
  const seriesName = seriesLabelMap[active.name as SeriesKey] ?? active.name;
  // Answer-first: bold headline (the answer) plus a smaller detail
  // line. The "likely" state's detail is mostly redundant with the KPI
  // cards below — kept for screen-readers and PNG export so the answer
  // travels with the snapshot. The "unlikely" state's detail carries
  // the % crossed signal that the cards can't show (P50 collapses to
  // the horizon clamp), so it earns its space.
  // Years framing — runway question is fundamentally a duration, so
  // P50 days → years (one decimal) is the natural unit for the
  // headline. Max-horizon days → years for the unlikely state.
  const p50Years = (active.p50_days / 365).toFixed(1);
  const maxYears = (data.max_horizon_days / 365).toFixed(1);
  const sharedVars = {
    series:        seriesName,
    target:        data.target_chf.toLocaleString(),
    max_days:      data.max_horizon_days,
    max_years:     maxYears,
    pct:           (active.p_crossed_by_max_horizon * 100).toFixed(1),
    p50_date:      shortDate(active.p50_date ?? ''),
    p50_days:      active.p50_days,
    p5_date:       shortDate(active.p5_date ?? ''),
    p95_date:      shortDate(active.p95_date ?? ''),
    years:         p50Years,
    n_projected:   data.n_users_projected.toLocaleString(),
  };
  const isUnlikely = active.p_crossed_by_max_horizon < 0.01;
  const headlineKey = isUnlikely ? 'summary_unlikely_headline' : 'summary_likely_headline';
  const detailKey   = isUnlikely ? 'summary_unlikely_detail'   : 'summary_likely_detail';
  const headline = useFinopsLabel(headlineKey, sharedVars);
  const detail   = useFinopsLabel(detailKey,   sharedVars);
  const toneClasses = isUnlikely
    ? 'border-amber-500/40 bg-amber-500/10'
    : 'border-primary/40 bg-primary/10';
  return (
    <div className={cn('rounded-md border px-3 py-1.5 text-foreground leading-snug', toneClasses)}>
      <span className="text-[1em] font-semibold">{headline}</span>
      <span className="text-[0.85em] text-foreground/80 ml-2">{detail}</span>
    </div>
  );
}

function SeriesPicker({
  value,
  onChange,
  series,
}: {
  value: SeriesKey;
  onChange: (k: SeriesKey) => void;
  series: CostTimeToTargetSeries[];
}) {
  // Filter to only series the response actually carries (defensive; today
  // always 4) and render in the canonical order.
  const present = new Set(series.map((s) => s.name));
  const order: SeriesKey[] = ['total', 'fixed', 'gemini', 'vertex'];
  const seriesLabel = useFinopsLabel('series_label');
  return (
    <div className="flex items-center gap-1.5 text-[0.85em]">
      <span className="text-muted-foreground">{seriesLabel}</span>
      {order.filter((k) => present.has(k)).map((k) => (
        <SeriesPickerChip
          key={k}
          name={k}
          on={value === k}
          onSelect={() => onChange(k)}
        />
      ))}
    </div>
  );
}

function SeriesPickerChip({
  name,
  on,
  onSelect,
}: {
  name: SeriesKey;
  on: boolean;
  onSelect: () => void;
}) {
  const spec = SERIES_SPECS[name];
  const Icon = spec.icon;
  const label = useFinopsLabel(spec.labelKey);
  return (
    <button
      onClick={onSelect}
      className={cn(
        'flex items-center gap-1 px-1.5 py-0.5 rounded border transition-colors',
        on
          ? 'border-primary bg-primary/10 text-primary font-medium'
          : 'border-border bg-card text-muted-foreground hover:border-primary/50',
      )}
    >
      <Icon className="h-3 w-3" />
      {label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// TargetControl — quick chips to flip between common targets
// ─────────────────────────────────────────────────────────────────────

function TargetControl({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  // Runway-scale cash balances. Investor framing — "what's our runway
  // from CHF 100k cash" — not LLM-budget framing.
  const presets = [50_000, 100_000, 250_000, 500_000, 1_000_000];
  const label = useFinopsLabel('cash_label');
  return (
    <div className="flex items-center gap-1.5 text-[0.85em]">
      <span className="text-foreground/80">{label}</span>
      {presets.map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          className={cn(
            'px-1.5 py-0.5 rounded border transition-colors',
            value === p
              ? 'border-primary bg-primary/10 text-primary font-medium'
              : 'border-border bg-card text-foreground/80 hover:border-primary/50',
          )}
        >
          {p.toLocaleString()}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// UserScaleControl — pick the projected user count
// ─────────────────────────────────────────────────────────────────────
//
// Substrate-driven: chips are multiples of n_users_observed (the
// registered-actor count from the graph). Selecting a chip overrides
// n_users_projected; "1×" sends nothing → server uses the substrate
// baseline. Keeps the math honest — there's always a real number on
// screen, never a hand-wavy "estimate".

function UserScaleControl({
  observed,
  projected,
  selected,
  onChange,
}: {
  observed: number;
  projected: number;
  selected: number | null;
  onChange: (v: number | null) => void;
}) {
  const baseline = Math.max(observed, 1);
  const scaleLabel = useFinopsLabel('scale_label', { observed: baseline });
  const varianceHint = useFinopsLabel('scale_variance_hint');
  const fixedUpliftHint = useFinopsLabel('scale_fixed_uplift_note', { factor: 3 });
  // Absolute-count ladder — today's baseline + forward targets above it.
  const FORWARD_TARGETS = [50, 100, 500, 1000];
  const targets: Array<{ count: number; value: number | null; isToday: boolean }> = [
    { count: baseline, value: null, isToday: true },
    ...FORWARD_TARGETS.filter((n) => n > baseline).map((n) => ({ count: n, value: n, isToday: false })),
  ];
  return (
    <div className="flex items-center gap-1.5 text-[0.85em]" title={`${scaleLabel} ${varianceHint}${fixedUpliftHint}`}>
      <span className="text-foreground/80 flex items-center">
        <Users className="h-3 w-3" />
      </span>
      {targets.map((t) => (
        <UserScaleChip
          key={t.count}
          count={t.count}
          value={t.value}
          baseline={baseline}
          isToday={t.isToday}
          on={(selected ?? null) === t.value}
          onSelect={() => onChange(t.value)}
        />
      ))}
    </div>
  );
}

function UserScaleChip({
  count,
  value,
  baseline,
  isToday,
  on,
  onSelect,
}: {
  count: number;
  value: number | null;
  baseline: number;
  isToday: boolean;
  on: boolean;
  onSelect: () => void;
}) {
  const todayLabel   = useFinopsLabel('scale_chip_today', { count });
  const forwardLabel = useFinopsLabel('scale_chip',       { count });
  const label = isToday ? todayLabel : forwardLabel;
  const tooltip = useFinopsLabel('scale_chip_tooltip', { count, baseline });
  return (
    <button
      onClick={onSelect}
      className={cn(
        'px-1.5 py-0.5 rounded border transition-colors',
        on
          ? 'border-primary bg-primary/10 text-primary font-medium'
          : 'border-border bg-card text-foreground/80 hover:border-primary/50',
      )}
      title={tooltip}
    >
      {label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ScalarStrip — three KPI tiles below the chart
// ─────────────────────────────────────────────────────────────────────

function ScalarStrip({ active }: { active: CostTimeToTargetSeries }) {
  const labelP5  = useFinopsLabel('tts_kpi_p5_earliest');
  const labelP50 = useFinopsLabel('tts_kpi_p50_median');
  const labelP95 = useFinopsLabel('tts_kpi_p95_latest');
  const hintP5  = useFinopsLabel('tts_kpi_day_hint', { n: active.p5_days });
  const hintP50 = useFinopsLabel('tts_kpi_day_hint', { n: active.p50_days });
  const hintP95 = useFinopsLabel('tts_kpi_day_hint', { n: active.p95_days });
  return (
    <div className="grid grid-cols-3 gap-3">
      <ScalarTile label={labelP5}  value={active.p5_date}  hint={hintP5}  tone="amber" />
      <ScalarTile label={labelP50} value={active.p50_date} hint={hintP50} tone="primary" />
      <ScalarTile label={labelP95} value={active.p95_date} hint={hintP95} tone="rose" />
    </div>
  );
}

function ScalarTile({
  label, value, hint, tone,
}: {
  label: string;
  value: string | null;
  hint: string;
  tone: 'amber' | 'primary' | 'rose';
}) {
  const toneClass = tone === 'amber'
    ? 'text-amber-600 dark:text-amber-400'
    : tone === 'rose'
      ? 'text-rose-600 dark:text-rose-400'
      : 'text-primary';
  return (
    <div className="rounded-lg border border-border bg-card/50 p-3">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-foreground/80">{label}</div>
      <div className={cn('text-lg font-semibold tabular-nums mt-0.5', toneClass)}>
        {value ?? '—'}
      </div>
      <div className="text-[11px] text-foreground/70 mt-0.5">{hint}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

function yearScopeDays(): { windowDays: number; horizonDays: number } {
  const now = new Date();
  const year = now.getFullYear();
  const todayUTC = Date.UTC(year, now.getMonth(), now.getDate());
  const jan1UTC  = Date.UTC(year, 0, 1);
  const dec31UTC = Date.UTC(year, 11, 31);
  const windowDays  = Math.max(1, Math.round((todayUTC - jan1UTC)  / 86_400_000));
  const horizonDays = Math.max(1, Math.round((dec31UTC - todayUTC) / 86_400_000));
  return { windowDays, horizonDays };
}

function shortDate(iso: string): string {
  if (!iso || iso.length < 10) return iso || '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
}

/**
 * Map a quantile day-offset to the histogram-bin date that contains it,
 * so the ReferenceLine x-value matches an actual bar's date_mid (recharts
 * categorical x-axis only renders ReferenceLines on existing categories).
 */
function nearestBinDate(histogram: CostTimeToTargetSeries['histogram'], dayOffset: number): string {
  for (const b of histogram) {
    if (b.day_offset_low <= dayOffset && dayOffset <= b.day_offset_high) return b.date_mid;
  }
  // Fallback — closest by midpoint distance
  let best = histogram[0];
  let bestDiff = Math.abs(best.day_offset_mid - dayOffset);
  for (const b of histogram) {
    const diff = Math.abs(b.day_offset_mid - dayOffset);
    if (diff < bestDiff) { best = b; bestDiff = diff; }
  }
  return best.date_mid;
}
